import Container from "../element/container";
import { useDispatch, useSelector } from "react-redux";
import Lightbox, { createIcon, IconButton } from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import { closeFullSizeGallery } from "../../redux/reducer/common.reducer";
import { lazy, Suspense, useEffect, useState } from "react";

import "yet-another-react-lightbox/styles.css";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/plugins/captions.css";
import { PDF_ZOOMS } from "../../helpers/gallery.media";

const GalleryPdf = lazy(() => import("./gallery.pdf"));

const ZoomInIcon = createIcon(
  "ZoomIn",
  <>
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
    <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z" />
  </>,
);
const ZoomOutIcon = createIcon(
  "ZoomOut",
  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14zM7 9h5v1H7z" />,
);
const OpenIcon = createIcon(
  "Open",
  <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />,
);

export default function FullSizeGallery() {
  const dispatch = useDispatch();
  const { fullSizeGallery, isOpenFullSizeGallery } = useSelector(
    (state) => state.common,
  );

  const handleCloseGallery = () => {
    dispatch(closeFullSizeGallery());
  };

  const [frameEl, setFrameEl] = useState(null);
  const [current, setCurrent] = useState(0);
  const [pdfZoom, setPdfZoom] = useState(PDF_ZOOMS[0]);

  useEffect(() => {
    const el = document.getElementById("mobile-frame");
    setFrameEl(el);
  }, []);

  const currentSlide = fullSizeGallery?.[current];
  const isPdf = currentSlide?.type === "pdf";
  const single = fullSizeGallery?.length === 1;

  return (
    fullSizeGallery?.length > 0 && (
      <Container tag="div">
        <Lightbox
          portal={{ root: frameEl }}
          open={isOpenFullSizeGallery}
          close={() => handleCloseGallery()}
          plugins={[Video, Zoom, Captions]}
          slides={fullSizeGallery}
          on={{
            view: ({ index }) => {
              setCurrent(index);
              setPdfZoom(PDF_ZOOMS[0]);
            },
          }}
          toolbar={{
            buttons: [
              isPdf && (
                <a
                  key="open"
                  className="yarl__button gallery-open"
                  href={currentSlide.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open PDF"
                  title="Open PDF"
                >
                  <OpenIcon className="yarl__icon" />
                </a>
              ),
              "close",
            ],
          }}
          // A single file has nowhere to go, so it gets no arrows and no swipe.
          carousel={{ finite: single }}
          render={{
            ...(single && { buttonPrev: () => null, buttonNext: () => null }),
            slide: ({ slide, offset, rect }) => slide.type === "pdf" ? (
              offset === 0 ? (
                <Suspense fallback={null}>
                  <GalleryPdf
                    key={slide.src}
                    slide={slide}
                    rect={rect}
                    zoom={pdfZoom}
                    onZoom={setPdfZoom}
                  />
                </Suspense>
              ) : <span />
            ) : undefined,
            // One pair of zoom buttons for every slide: the lightbox zooms
            // photos itself, and a PDF is zoomed by re-drawing its pages.
            buttonZoom: ({ zoom, minZoom, maxZoom, zoomIn, zoomOut, disabled }) => {
              const canZoomIn = isPdf
                ? pdfZoom < PDF_ZOOMS[PDF_ZOOMS.length - 1]
                : !disabled && zoom < maxZoom;
              const canZoomOut = isPdf
                ? pdfZoom > PDF_ZOOMS[0]
                : !disabled && zoom > minZoom;
              return (
                <>
                  <IconButton
                    key="zoom-in"
                    label="Zoom in"
                    icon={ZoomInIcon}
                    disabled={!canZoomIn}
                    onClick={
                      isPdf
                        ? () => setPdfZoom(PDF_ZOOMS.find((step) => step > pdfZoom))
                        : zoomIn
                    }
                  />
                  <IconButton
                    key="zoom-out"
                    label="Zoom out"
                    icon={ZoomOutIcon}
                    disabled={!canZoomOut}
                    onClick={
                      isPdf
                        ? () =>
                            setPdfZoom(
                              [...PDF_ZOOMS].reverse().find((step) => step < pdfZoom),
                            )
                        : zoomOut
                    }
                  />
                </>
              );
            },
          }}
          captions={{ showToggle: false, descriptionTextAlign: "start" }}
          // Photos are often no larger than the screen; without a higher
          // limit the lightbox has nothing left to zoom into.
          zoom={{ maxZoomPixelRatio: 4 }}
          video={{
            controls: true,
            playsInline: true,
            autoPlay: true,
            // loop: true,
            muted: true,
          }}
        />
      </Container>
    )
  );
}
